import { useCallback, useEffect, useRef, useState } from "react";
import pb, { PRODUCTS_COLLECTION } from "../lib/pocketbase";
import { mapProduct } from "./mapProduct";
import snapshot from "./products.snapshot.json";

// Bundled fallback so the storefront renders immediately and survives a
// PocketBase cold start or outage. Refresh it with:
//   node scripts/snapshot-products.mjs
const FALLBACK = snapshot.map(mapProduct);

const useProducts = () => {
  const [products, setProducts] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const alive = useRef(true);

  const load = useCallback(
    () =>
      pb
        .collection(PRODUCTS_COLLECTION)
        .getFullList({ sort: "sortOrder" })
        .then((records) => {
          if (!alive.current) return;
          setProducts(records.map(mapProduct));
          setStale(false);
          setLoading(false);
        })
        .catch((error) => {
          if (!alive.current || error?.isAbort) return;
          // Keep showing the bundled snapshot rather than an empty store.
          console.warn("Falling back to bundled catalog:", error?.message);
          setStale(true);
          setLoading(false);
        }),
    []
  );

  useEffect(() => {
    alive.current = true;
    // `loading` ends when a fetch succeeds or really fails. The SDK cancels a
    // duplicate in-flight request (this one, when the realtime refetch below
    // starts), and that abort must not count as "loaded": product links
    // would then be resolved against the bundled snapshot only.
    load();

    // Live updates: edit a product in the PocketBase dashboard and every open
    // storefront reflects it without a refresh. Bulk edits are coalesced so a
    // burst of changes triggers one refetch.
    let timer = null;
    let unsubscribe = null;

    pb.collection(PRODUCTS_COLLECTION)
      .subscribe("*", () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (alive.current) load();
        }, 400);
      })
      .then((fn) => {
        unsubscribe = fn;
        // Subscribing may have taken a while; catch anything missed meanwhile.
        if (alive.current) load();
      })
      .catch((error) => {
        // Realtime is a nicety — the initial fetch already succeeded.
        console.warn("Live catalog updates unavailable:", error?.message);
      });

    return () => {
      alive.current = false;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
    };
  }, [load]);

  return { products, loading, stale, refresh: load };
};

export default useProducts;
